import {
  AppColumnDef,
  ColumnOrderState,
  ColumnVisibilityState,
} from '../appTable';
import { useMemo } from 'react';
import { logger } from '../../../../services/logger';
import { getColumnId } from '../tableUtilsNew';
import {
  ColumnCatalog,
  ColumnContextBase,
  TableViewColumnSelection,
} from './types';

export interface UseTableColumnsResult<TData extends object> {
  /** Final `ColumnDef[]` for the `DataTable` `columns` prop. */
  columns: AppColumnDef<TData, any>[];
  /**
   * TanStack state slice for the `DataTable` `state` prop. Carries
   * `columnVisibility` + `columnOrder` derived from the catalog and the
   * optional `view`. With no view this is behavior-identical to passing the
   * built columns alone.
   */
  state: {
    columnVisibility: ColumnVisibilityState;
    columnOrder: ColumnOrderState;
  };
}

/**
 * Turns a column catalog + runtime context (+ optional view column selection)
 * into the `columns` and `state` a `DataTable` consumes. Drop-in replacement
 * for the `useMemo(() => [...cols], deps)` pattern used across list pages.
 *
 * Memoization contract: catalogs are module-scope constants, so pass a
 * memoized `context` (mirror the deps you would have listed on the old column
 * `useMemo`) and columns rebuild on exactly the same cadence as before.
 *
 * View resolution rules (so future persisted views "just work"):
 * - Unknown or currently unavailable ids in `view.columnIds` are ignored for
 *   rendering — never repaired or stripped from the saved view.
 * - Catalog columns absent from `view.columnIds` are hidden (`columnIds` is a
 *   positive selection); newly added catalog columns therefore stay hidden in
 *   existing views until selected.
 * - `customizable: false` entries are always visible and stay anchored at
 *   their catalog-relative positions regardless of the view.
 * - A view resolving to zero visible customizable columns falls back to
 *   catalog defaults.
 */
export function useTableColumns<
  TData extends object,
  TContext extends ColumnContextBase<TData>,
>(
  catalog: ColumnCatalog<TData, TContext>,
  context: TContext,
  view?: TableViewColumnSelection,
): UseTableColumnsResult<TData> {
  // Availability is checked FIRST — unavailable entries are never built.
  const availableEntries = useMemo(
    () =>
      catalog.filter((entry) =>
        entry.isAvailable ? entry.isAvailable(context) : true,
      ),
    [catalog, context],
  );

  const columns = useMemo(
    () =>
      availableEntries.map((entry) => {
        const columnDef = entry.build(context);
        const resolvedId = getColumnId(columnDef);
        if (resolvedId !== entry.id) {
          logger.error(
            `[columnCatalog] Entry "${entry.id}" builds a ColumnDef whose ` +
              `TanStack id is "${resolvedId}". These MUST match or filter, ` +
              `visibility, and order state will desync.`,
          );
        }
        return columnDef;
      }),
    [availableEntries, context],
  );

  // The view's ordered visible customizable ids, resolved against what is
  // currently available. `undefined` means "no view" — catalog defaults apply
  // (including the degenerate case where nothing in the view resolves).
  const resolvedViewColumnIds = useMemo<string[] | undefined>(() => {
    if (!view?.columnIds?.length) {
      return undefined;
    }
    const customizableIds = new Set(
      availableEntries
        .filter(({ customizable }) => customizable !== false)
        .map(({ id }) => id),
    );
    const visibleIds = view.columnIds.filter((id) => customizableIds.has(id));
    return visibleIds.length ? visibleIds : undefined;
  }, [availableEntries, view?.columnIds]);

  const columnOrder = useMemo<ColumnOrderState>(() => {
    const availableIds = availableEntries.map(({ id }) => id);
    if (!resolvedViewColumnIds) {
      return availableIds;
    }
    const resolvedIdSet = new Set(resolvedViewColumnIds);
    const unlistedCustomizableIds = availableEntries
      .filter(
        ({ id, customizable }) =>
          customizable !== false && !resolvedIdSet.has(id),
      )
      .map(({ id }) => id);
    // Customizable columns take the view's order (hidden ones append in
    // catalog order); structural columns stay at their catalog slots.
    const orderedCustomizableIds = [
      ...resolvedViewColumnIds,
      ...unlistedCustomizableIds,
    ];
    let customizableIndex = 0;
    return availableEntries.map((entry) => {
      if (entry.customizable === false) {
        return entry.id;
      }
      const orderedId = orderedCustomizableIds[customizableIndex];
      customizableIndex += 1;
      return orderedId;
    });
  }, [availableEntries, resolvedViewColumnIds]);

  const columnVisibility = useMemo<ColumnVisibilityState>(() => {
    const visibleIdSet = resolvedViewColumnIds
      ? new Set(resolvedViewColumnIds)
      : undefined;
    return Object.fromEntries(
      availableEntries.map((entry) => {
        if (entry.customizable === false) {
          return [entry.id, true];
        }
        if (visibleIdSet) {
          return [entry.id, visibleIdSet.has(entry.id)];
        }
        return [entry.id, entry.defaultVisible !== false];
      }),
    );
  }, [availableEntries, resolvedViewColumnIds]);

  const state = useMemo(
    () => ({ columnVisibility, columnOrder }),
    [columnVisibility, columnOrder],
  );

  return { columns, state };
}
